import { calculateEnergyEfficiency } from "./calculateEnergyEfficiency.ts";
import { SolutionMap, SolutionOptionKey } from "@labs/shared/types/Solution.ts";
import {
  dieselConversionFactor,
  electricConversionFactor,
  fuelOilConversionFactor,
  gasolineConversionFactor,
  naturalGasConversionFactor,
  propaneConversionFactor,
} from "../../data/planAnalysisConstants.ts";
import { FuelSource } from "@labs/shared/types/FuelSource.ts";
import {
  EfficiencyResultsByFuel,
  NullablePlanComponent,
  PlanComponentCategory,
} from "@labs/shared/types/AnalyzedPlan.ts";
import { EmissionsBySource } from "@labs/shared/types/EmissionsBySource.ts";

export const getEfficiencyComponentAndResultsByFuel = (
  solutionMap: SolutionMap,
  baselineCarbonWithGrowthAndCleanerGrid: EmissionsBySource,
): {
  efficiencyComponent: NullablePlanComponent;
  efficiencyResultsByFuel: EfficiencyResultsByFuel;
} => {
  // Energy efficiency calculations
  const electricityEfficiencyResults = calculateEnergyEfficiency(
    solutionMap,
    SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryElectricity,
    baselineCarbonWithGrowthAndCleanerGrid.electricity,
    electricConversionFactor,
    FuelSource.Electricity,
  );
  const naturalGasEfficiencyResults = calculateEnergyEfficiency(
    solutionMap,
    SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryNaturalGas,
    baselineCarbonWithGrowthAndCleanerGrid.naturalGas,
    naturalGasConversionFactor,
    FuelSource.NaturalGas,
  );
  const propaneEfficiencyResults = calculateEnergyEfficiency(
    solutionMap,
    SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryPropane,
    baselineCarbonWithGrowthAndCleanerGrid.propane,
    propaneConversionFactor,
    FuelSource.Propane,
  );
  const fuelOilEfficiencyResults = calculateEnergyEfficiency(
    solutionMap,
    SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryFuelOil,
    baselineCarbonWithGrowthAndCleanerGrid.fuelOil,
    fuelOilConversionFactor,
    FuelSource.FuelOil,
  );
  const dieselEfficiencyResults = calculateEnergyEfficiency(
    solutionMap,
    SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryDiesel,
    baselineCarbonWithGrowthAndCleanerGrid.diesel,
    dieselConversionFactor,
    FuelSource.Diesel,
  );
  const gasolineEfficiencyResults = calculateEnergyEfficiency(
    solutionMap,
    SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryGasoline,
    baselineCarbonWithGrowthAndCleanerGrid.gasoline,
    gasolineConversionFactor,
    FuelSource.Gasoline,
  );

  const efficiencyResults = [
    electricityEfficiencyResults,
    naturalGasEfficiencyResults,
    propaneEfficiencyResults,
    fuelOilEfficiencyResults,
    dieselEfficiencyResults,
    gasolineEfficiencyResults,
  ];

  const relevantEfficiencyResults = efficiencyResults.filter(
    (result) => result.solutionPercent > 0,
  );

  const efficiencyComponent: NullablePlanComponent =
    relevantEfficiencyResults.length > 0
      ? {
          planComponentCategory: PlanComponentCategory.CutEnergyWaste,
          intermediateCostScore: relevantEfficiencyResults.reduce(
            (acc, result) => acc + result.costScore,
            0,
          ),
          carbonImpact: relevantEfficiencyResults.reduce(
            (acc, result) => acc + result.carbonImpact,
            0,
          ),
          fuelImpacts: relevantEfficiencyResults.flatMap(
            (result) => result.fuelImpacts,
          ),
        }
      : null;

  return {
    efficiencyComponent,
    efficiencyResultsByFuel: {
      [FuelSource.Electricity]: electricityEfficiencyResults,
      [FuelSource.NaturalGas]: naturalGasEfficiencyResults,
      [FuelSource.Propane]: propaneEfficiencyResults,
      [FuelSource.FuelOil]: fuelOilEfficiencyResults,
      [FuelSource.Diesel]: dieselEfficiencyResults,
      [FuelSource.Gasoline]: gasolineEfficiencyResults,
    },
  };
};
